// StudentSignup.container: aspirant signup wizard; account and profile-registration use separate URLs.
// Mistake: profile-step useMemo was placed after GlobalLoading early return, breaking Rules of Hooks; keep derived-step flags before any conditional return.
// Mistake: NEEDS_SOCIAL_LINK_CONFIRM only opened a modal; redirect to oauth-link and merge accountName/accountPicture from API payload into query.
// Mistake: createInfo called validateTermsForSubmit; termList stays unchecked when resuming after account, blocking finish though terms were saved at signup.
// Mistake: Whole wizard gated on termList.length; profile+ steps showed blank until getTermList finished—gate only account and show GlobalLoading there.
// Mistake: dev slug used date+time string that could exceed 16 chars or fail slug regex; use prefix + Date.now() so checkStudentSlug always passes.
// Mistake: success screen showed email verification copy for OAuth signup; pass hideEmailVerificationNotice when oauthSignupMeta is set.
// Mistake: oauthSignupMeta can be cleared before done screen; also hide txtDesc3 when Redux signupTypeCode is signupType:socialAccount after createInfo.
// Mistake: OAuth callback errors kept users on /register/account; unrecoverable social mismatch should return to signup method selection.
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import * as commonData from "data/common.data";
import { getCurrentLangName } from "helpers/String.helper";
import { checkIsEmail, checkPassword, checkIsNumberThatDoNotBeginWithZero, checkStudentSlug, checkStudentSlugText } from "helpers/Reg.helper";
import { getAccessToken, removeAuthTokens, setAccessToken } from "helpers/Auth.helper";
import * as ImageHelper from "helpers/Image.helper";

import * as AuthApi from "api/common/Auth.api";
import * as DataApi from "api/common/Data.api";
import * as StudentApi from "api/student/Student.api";
import { setInfo as setUserInfo, setIsLogin } from "reducers/User";

import PublicProfileImageCropModalComponent from "pages/student/v2/profile/PublicProfileImageCropModal.component";

import StudentSignupComponent from "./StudentSignup.component";
import StudentSignupDoneComponent from "./StudentSignupDone.component";
import GlobalLoadingContainer from "pages/common/loading/GlobalLoading.container";

const REGISTER_STEPS = ["account", "profile", "basic", "specialty", "contact", "confirm"] as const;
type StudentSignupRegisterStep = (typeof REGISTER_STEPS)[number];

const STUDENT_SIGNUP_REGISTER_BASE = "/signup/student/register";
const STUDENT_SIGNUP_ACCOUNT_PATH = `${STUDENT_SIGNUP_REGISTER_BASE}/account`;
const STUDENT_SIGNUP_PROFILE_PATH = `${STUDENT_SIGNUP_REGISTER_BASE}/profile`;

/** Parse register URLs to wizard step while supporting legacy step paths. */
const stepFromRegisterPathname = (
    pathname: string,
    registerBasePath: string,
    accountPath: string,
    profilePath: string,
    forceInfoRegistrationFlow: boolean
): StudentSignupRegisterStep | null => {
    const normalized = pathname.replace(/\/+$/, "") || "/";
    if (!normalized.startsWith(registerBasePath)) {
        return null;
    }
    if (!forceInfoRegistrationFlow && (normalized === registerBasePath || normalized === accountPath)) {
        return "account";
    }
    if (normalized === profilePath) {
        return "profile";
    }
    const suffix = normalized.slice(registerBasePath.length + 1);
    if (!suffix || (!forceInfoRegistrationFlow && suffix.split("/")[0] === "oauth-link")) {
        return null;
    }
    const seg = suffix.split("/")[0] || "";
    if (forceInfoRegistrationFlow && seg === "account") {
        return "profile";
    }
    return (REGISTER_STEPS as readonly string[]).includes(seg) ? (seg as StudentSignupRegisterStep) : null;
};

type OauthSignupMeta = {
    socialId: string;
    socialAccountType: string;
    signupUuid?: string;
};

const resolveOauthErrorMessage = (oauthError: string): string => {
    if (oauthError === "OAUTH_LINE_EMAIL_REQUIRED") {
        return getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.errLineNoEmailForSignup");
    }
    return oauthError;
};

const oauthProviderToSocialAccountType = (provider: string): string => {
    const map: Record<string, string> = {
        google: "socialAccountType:google",
        kakao: "socialAccountType:kakao",
        line: "socialAccountType:line",
        apple: "socialAccountType:apple",
    };
    return map[provider] || `socialAccountType:${provider}`;
};

/** Stage fixture image for non-prod header autofill. */
const STUDENT_SIGNUP_DEV_AUTOFILL_IMAGE = {
    url: "https://thpe-public-stage.s3.ap-northeast-2.amazonaws.com/image/1776300214047/8/c/a/3/6/d/m/x/7/b/u/l/w/y/4/u/f7ccc6a258014070aadb93d69325001f.jpg",
    id: "2a62f03861104212886abdb460d91813",
} as const;

/** Dummy autofill when REACT_APP_ENV is not prod. */
const isStudentSignupDevAutofillEnabled = () => process.env.REACT_APP_ENV !== "prod";

const pad2 = (n: number) => String(n).padStart(2, "0");

const buildStudentSignupDevAutofillPayload = (ts: number) => {
    const now = new Date();
    const yy = now.getFullYear().toString().slice(2);
    const nickname = `${yy}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
    const slug = `d${ts}`;

    const labelIncludes = (names: any, needles: string[]) => {
        const ko = String(getCurrentLangName(names) || "");
        const en = String(names?.en || "");
        return needles.some((n) => ko.includes(n) || en.toLowerCase().includes(n.toLowerCase()));
    };

    const specialtyList = commonData.getCommonCodeListByParent("specialty") || [];
    const vocalCode = specialtyList.find((s: any) => labelIncludes(s.names, ["보컬", "Vocal", "vocal"]))?.code;
    const rapCode = specialtyList.find((s: any) => labelIncludes(s.names, ["랩", "Rap", "rap"]))?.code;

    const langList = commonData.getLanguageCodeList() || [];
    const findLangCode = (...needles: string[]) =>
        langList.find((l: any) => needles.some((n) => l.code === n || labelIncludes(l.names, [n])))?.code || "";

    const levelList = commonData.getCommonCodeListByParent("languageLevel") || [];
    const findLevelCode = (...needles: string[]) =>
        levelList.find((lv: any) => labelIncludes(lv.names, needles))?.code || "languageLevel:3";

    const ko = findLangCode("ko", "한국") || "ko";
    const en = findLangCode("en", "영") || "en";
    const ja = findLangCode("ja", "일") || "ja";

    const mbtiList = commonData.getMbtiCodeList() || [];
    const enfpCode =
        mbtiList.find(
            (m: any) =>
                String(m.code || "").toLowerCase().includes("enfp") ||
                String(m.names?.en || "").toUpperCase().includes("ENFP")
        )?.code || "";

    const contactTypes = commonData.getCommonCodeListByParent("contactType") || [];
    const kakaoCode = contactTypes.find((c: any) => labelIncludes(c.names, ["카카오", "Kakao", "kakao"]))?.code;

    return {
        ts,
        nickname,
        slug,
        specialtyCodes: [vocalCode, rapCode].filter(Boolean) as string[],
        languageRows: [
            { id: `lang-dev-${ts}-0`, languageCode: ko, level: findLevelCode("원어민", "Native", "native") },
            { id: `lang-dev-${ts}-1`, languageCode: en, level: findLevelCode("중급", "Intermediate") },
            { id: `lang-dev-${ts}-2`, languageCode: ja, level: findLevelCode("초급", "Beginner") },
        ],
        contactTypeCode: kakaoCode || "",
        mbtiCode: enfpCode || mbtiList.find((m: any) => String(m.names?.en || "").toUpperCase() === "ENFP")?.code || "",
        countryCode: "KR",
        firstName: `희문${ts}`,
        lastName: `양${ts}`,
        birthday: "2010-05-02",
        genderTypeCode: "genderType:boy",
        height: "178",
        weight: "72",
        shoeSize: "265",
        bloodTypeCode: "bloodType:a",
        contact: "moonpace",
        parentEmail: `parent-autofill-${ts}@example.com`,
    };
};

const StudentSignupContainer = (props: any) => {
    const btnRef = useRef<any>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInfo = useSelector((state: any) => state.user?.info);
    const location = useLocation();
    const schoolIdFromQuery = useMemo(() => {
        const raw = new URLSearchParams(location.search).get("school-id");
        if (raw == null || raw === "" || Number.isNaN(Number(raw))) {
            return null;
        }
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    }, [location.search]);

    const { cancelSignup } = props;
    const registerBasePath = String(props?.registerBasePath || STUDENT_SIGNUP_REGISTER_BASE);
    const accountPath = String(props?.accountPath || `${registerBasePath}/account`);
    const profilePath = String(props?.profilePath || `${registerBasePath}/profile`);
    const signupMethodPath = String(props?.signupMethodPath || "/signup/student");
    const forceInfoRegistrationFlow = Boolean(props?.forceInfoRegistrationFlow);

    const [wizardStep, setWizardStep] = useState<StudentSignupRegisterStep>(() => {
        const parsed = stepFromRegisterPathname(
            location.pathname,
            registerBasePath,
            accountPath,
            profilePath,
            forceInfoRegistrationFlow
        );
        if (parsed) {
            return parsed;
        }
        return forceInfoRegistrationFlow ? "profile" : "account";
    });

    const [termList, setTermList] = useState([]);
    const [termsReady, setTermsReady] = useState(false);

    const [imageInfo, setImageInfo] = useState<any>({});
    const [isOnNetworking, setIsOnNetworking] = useState(false);

    const [imageIdList] = useState<any[]>([]);
    const [cropObjectUrl, setCropObjectUrl] = useState<string | null>(null);
    const pickedFileNameRef = useRef("");
    const [isDone, setIsDone] = useState(false);

    const [accountParams, setAccountParams] = useState<any>({
        username: "",
        password: "",
        confirmedPassword: "",
        accountTypeCode: "accountType:student",
    });
    const [usernameValidation, setUsernameValidation] = useState<{
        type: "idle" | "checking" | "error" | "success";
        message: string;
    }>({ type: "idle", message: "" });
    const [isUsernameAvailable, setIsUsernameAvailable] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState<{
        type: "idle" | "error" | "success";
        message: string;
    }>({ type: "idle", message: "" });
    const [confirmedPasswordValidation, setConfirmedPasswordValidation] = useState<{
        type: "idle" | "error" | "success";
        message: string;
    }>({ type: "idle", message: "" });

    const profileSlugCheckSeqRef = useRef(0);
    const [profileSlugValidation, setProfileSlugValidation] = useState<{
        type: "idle" | "checking" | "success" | "error";
        message: string;
        checkedSlug: string | null;
    }>({ type: "idle", message: "", checkedSlug: null });

    const [studentParams, setStudentParams] = useState<any>({
        profileImageId: null,
        countryCode: "",
        firstName: "",
        lastName: "",
        birthday: "",
        genderTypeCode: "",
        slug: "",
        schoolId: schoolIdFromQuery,
        nickname: "",
        height: "",
        weight: "",
        shoeSize: "",
        mbtiCode: "",
        specialtyCodeList: null,
        bloodTypeCode: "",
        imageIdList: [],
        contactTypeCode: "",
        contact: "",
    });

    const [selectedSpecialtyCodes, setSelectedSpecialtyCodes] = useState<string[]>([]);

    const [parentEmail, setParentEmail] = useState("");
    const [languageRows, setLanguageRows] = useState<{ id: string; languageCode: string; level: string }[]>([]);
    const [oauthSignupMeta, setOauthSignupMeta] = useState<OauthSignupMeta | null>(null);

    useEffect(() => {
        setStudentParams((prev: any) => ({ ...prev, schoolId: schoolIdFromQuery }));
    }, [schoolIdFromQuery]);

    const applyStudentSignupDevAutofill = useCallback(() => {
        // 빌드 시 주입된 값; 클릭할 때마다 항상 출력 (prod/stage 무관)
        console.log("[StudentSignup] REACT_APP_ENV:", process.env.REACT_APP_ENV);
        if (!isStudentSignupDevAutofillEnabled()) {
            return;
        }
        const ts = Date.now();
        const p = buildStudentSignupDevAutofillPayload(ts);
        setImageInfo({ id: STUDENT_SIGNUP_DEV_AUTOFILL_IMAGE.id, url: STUDENT_SIGNUP_DEV_AUTOFILL_IMAGE.url });
        setStudentParams((prev: any) => ({
            ...prev,
            countryCode: p.countryCode,
            firstName: p.firstName,
            lastName: p.lastName,
            birthday: p.birthday,
            genderTypeCode: p.genderTypeCode,
            slug: p.slug,
            nickname: p.nickname,
            height: p.height,
            weight: p.weight,
            shoeSize: p.shoeSize,
            mbtiCode: p.mbtiCode,
            bloodTypeCode: p.bloodTypeCode,
            contactTypeCode: p.contactTypeCode,
            contact: p.contact,
        }));
        setSelectedSpecialtyCodes(p.specialtyCodes);
        setLanguageRows(p.languageRows);
        setParentEmail(p.parentEmail);
        const slugForCheck = String(p.slug || "").trim();
        profileSlugCheckSeqRef.current += 1;
        void (async () => {
            const seq = profileSlugCheckSeqRef.current;
            if (!slugForCheck) {
                setProfileSlugValidation({ type: "idle", message: "", checkedSlug: null });
                return;
            }
            if (!checkStudentSlug(slugForCheck)) {
                if (seq === profileSlugCheckSeqRef.current) {
                    setProfileSlugValidation({
                        type: "error",
                        message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidSlug"),
                        checkedSlug: null,
                    });
                }
                return;
            }
            try {
                const taken = await AuthApi.checkExistsSlug({ slug: slugForCheck });
                if (seq !== profileSlugCheckSeqRef.current) {
                    return;
                }
                if (taken) {
                    setProfileSlugValidation({
                        type: "error",
                        message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errExistsSlug"),
                        checkedSlug: null,
                    });
                    return;
                }
                setProfileSlugValidation({ type: "success", message: "", checkedSlug: slugForCheck });
            } catch (e) {
                console.error("StudentSignupContainer:devAutofillSlugCheck", e);
                if (seq === profileSlugCheckSeqRef.current) {
                    setProfileSlugValidation({ type: "idle", message: "", checkedSlug: null });
                }
            }
        })();
    }, []);

    const closeCropModal = () => {
        if (cropObjectUrl) {
            URL.revokeObjectURL(cropObjectUrl);
        }
        setCropObjectUrl(null);
    };

    const onProfileImageFileChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const file = evt.target.files?.[0];
        evt.target.value = "";
        if (!file) return;

        if (!/^image\/(jpeg|jpg|png)$/i.test(file.type)) {
            alert(getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.txtCropInvalidFormat"));
            return;
        }

        pickedFileNameRef.current = file.name;
        setCropObjectUrl(URL.createObjectURL(file));
    };

    const onCropModalApply = async (blob: Blob) => {
        const rawName = pickedFileNameRef.current || "profile";
        const base = rawName.replace(/\.[^.]+$/i, "") || "profile";
        const file = new File([blob], `${base}.jpg`, { type: "image/jpeg" });
        closeCropModal();
        setIsOnNetworking(true);

        try {
            const preview = await ImageHelper.getImageInfo(file);
            if (!preview) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "validation.txtChooseImage"));
                return;
            }

            const result = await DataApi.uploadImage(file);
            setImageInfo({ id: result.id, url: preview.url });
        } catch (err) {
            console.log("StudentSignupContainer:onCropModalApply", err);
        } finally {
            setIsOnNetworking(false);
        }
    };

    const getTermList = async () => {
        if (forceInfoRegistrationFlow) {
            setTermsReady(true);
            return;
        }
        try {
            const result = await AuthApi.getTermListByType({ accountType: "wannabe" });
            if (result?.list) {
                result.list.map((item: any) => (item.checked = false));
                setTermList(result.list);
            }
        } catch (e) {
            console.error("StudentSignupContainer:getTermList", e);
        } finally {
            setTermsReady(true);
        }
    };

    const onChangeInput = (componentName: string, value: any) => {
        if (componentName === "username") {
            setAccountParams({ ...accountParams, username: value.trim() });
            setUsernameValidation({ type: "idle", message: "" });
            setIsUsernameAvailable(false);
        } else if (componentName === "password") {
            setAccountParams({ ...accountParams, password: value });
            setPasswordValidation({ type: "idle", message: "" });
            setConfirmedPasswordValidation({ type: "idle", message: "" });
        }
        if (componentName === "confirmedPassword") {
            setAccountParams({ ...accountParams, confirmedPassword: value });
            setConfirmedPasswordValidation({ type: "idle", message: "" });
        }

        if (componentName === "countryCode") {
            setStudentParams({ ...studentParams, countryCode: value });
        } else if (componentName === "firstName") {
            setStudentParams({ ...studentParams, firstName: value });
        } else if (componentName === "lastName") {
            setStudentParams({ ...studentParams, lastName: value });
        } else if (componentName === "birthday") {
            setStudentParams({ ...studentParams, birthday: value });
        } else if (componentName === "genderTypeCode") {
            setStudentParams({ ...studentParams, genderTypeCode: value });
        } else if (componentName === "nickname") {
            setStudentParams({ ...studentParams, nickname: value });
        } else if (componentName === "height") {
            const height = checkIsNumberThatDoNotBeginWithZero(value) ? value : studentParams.height;
            setStudentParams({ ...studentParams, height });
        } else if (componentName === "weight") {
            const weight = checkIsNumberThatDoNotBeginWithZero(value) ? value : studentParams.weight;
            setStudentParams({ ...studentParams, weight });
        } else if (componentName === "shoeSize") {
            const shoeSize = checkIsNumberThatDoNotBeginWithZero(value) ? value : studentParams.shoeSize;
            setStudentParams({ ...studentParams, shoeSize });
        } else if (componentName === "mbtiCode") {
            setStudentParams({ ...studentParams, mbtiCode: value });
        } else if (componentName === "specialtyCodeList") {
            setStudentParams({ ...studentParams, specialtyCodeList: value });
        } else if (componentName === "bloodTypeCode") {
            setStudentParams({ ...studentParams, bloodTypeCode: value });
        } else if (componentName === "slug") {
            if (value && !checkStudentSlugText(value)) {
                return false;
            }
            profileSlugCheckSeqRef.current += 1;
            const nextSlug = value;
            setStudentParams((prev: any) => ({ ...prev, slug: nextSlug }));
            setProfileSlugValidation((prev) => {
                const trimmed = String(nextSlug || "").trim();
                if (!trimmed) {
                    return { type: "idle", message: "", checkedSlug: null };
                }
                if (prev.type === "success" && prev.checkedSlug === trimmed) {
                    return prev;
                }
                return { type: "idle", message: "", checkedSlug: null };
            });
        } else if (componentName === "contactTypeCode") {
            setStudentParams({ ...studentParams, contactTypeCode: value });
        } else if (componentName === "contact") {
            setStudentParams({ ...studentParams, contact: value });
        }
    };

    const onChangeCheckbox = (componentName: string, checkedItem: any, checked: boolean) => {
        if (componentName === "specialty") {
            const code = String(checkedItem || "");
            setSelectedSpecialtyCodes((prev) => {
                if (!code) {
                    return prev;
                }
                if (checked) {
                    return prev.includes(code) ? prev : [...prev, code];
                }
                return prev.filter((c) => c !== code);
            });
        } else {
            setTermList((prev: any) => {
                const termList = [...prev];
                checkedItem === "all" ? termList.map((item: any) => (item.checked = checked)) : (termList[+checkedItem].checked = checked);

                return termList;
            });
        }
    };

    const checkIsUnder14 = (birthday: string) => {
        const today = new Date();
        const birthDate = new Date(birthday);

        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return 14 > age ? true : false;
    };

    const goToStep = (step: StudentSignupRegisterStep) => {
        setWizardStep(step);
    };

    const addLanguageRow = () => {
        setLanguageRows((rows) => [...rows, { id: `lang-${Date.now()}-${rows.length}`, languageCode: "", level: "languageLevel:3" }]);
    };

    const removeLanguageRow = (id: string) => {
        setLanguageRows((rows) => rows.filter((r) => r.id !== id));
    };

    const updateLanguageRow = (id: string, patch: Partial<{ id: string; languageCode: string; level: string }>) => {
        setLanguageRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    const validateAccountFields = () => {
        if (!accountParams.username) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmail"));
            return true;
        }
        if (!checkIsEmail(accountParams.username)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidEmail"));
            return true;
        }
        if (oauthSignupMeta) {
            if (oauthSignupMeta.socialAccountType === "socialAccountType:google" && !oauthSignupMeta.signupUuid) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmail"));
                return true;
            }
            return false;
        }
        if (!accountParams.password) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errPassword"));
            return true;
        }
        if (!checkPassword(accountParams.password)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidPassword"));
            return true;
        }
        if (!accountParams.confirmedPassword) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errConfirmPassword"));
            return true;
        }
        if (accountParams.password !== accountParams.confirmedPassword) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errPasswordAndConfirmPasswordMatch"));
            return true;
        }
        return false;
    };

    const validateUsernameOnBlur = async () => {
        const username = String(accountParams.username || "").trim();
        if (!username) {
            setIsUsernameAvailable(false);
            setUsernameValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmail"),
            });
            return;
        }
        if (!checkIsEmail(username)) {
            setIsUsernameAvailable(false);
            setUsernameValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidEmail"),
            });
            return;
        }

        setUsernameValidation({ type: "checking", message: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtChecking") });
        try {
            const res = await AuthApi.checkExistsUsername(username, "accountType:student", "signupType:email");
            if (res?.data?.data?.exists) {
                setIsUsernameAvailable(false);
                setUsernameValidation({
                    type: "error",
                    message: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtExistingEmail"),
                });
                return;
            }
            setIsUsernameAvailable(true);
            setUsernameValidation({ type: "idle", message: "" });
        } catch (e) {
            console.error("StudentSignupContainer:validateUsernameOnBlur", e);
            setIsUsernameAvailable(false);
            setUsernameValidation({ type: "error", message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmailCheckFailed") });
        }
    };

    const validatePasswordOnBlur = () => {
        const password = String(accountParams.password || "");
        if (!password) {
            setPasswordValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errPassword"),
            });
            return;
        }
        if (!checkPassword(password)) {
            setPasswordValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidPassword"),
            });
            return;
        }
        setPasswordValidation({ type: "success", message: "" });
    };

    const validateConfirmedPasswordOnBlur = () => {
        const password = String(accountParams.password || "");
        const confirmedPassword = String(accountParams.confirmedPassword || "");
        if (!confirmedPassword) {
            setConfirmedPasswordValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errConfirmPassword"),
            });
            return;
        }
        if (password !== confirmedPassword) {
            setConfirmedPasswordValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errPasswordAndConfirmPasswordMatch"),
            });
            return;
        }
        setConfirmedPasswordValidation({ type: "success", message: "" });
    };

    const validateProfileSlugOnBlur = useCallback(async (slugRaw: string) => {
        const slug = String(slugRaw || "").trim();
        profileSlugCheckSeqRef.current += 1;
        const seq = profileSlugCheckSeqRef.current;
        if (!slug) {
            setProfileSlugValidation({ type: "idle", message: "", checkedSlug: null });
            return;
        }
        if (!checkStudentSlug(slug)) {
            setProfileSlugValidation({
                type: "error",
                message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidSlug"),
                checkedSlug: null,
            });
            return;
        }
        setProfileSlugValidation({
            type: "checking",
            message: getLanguageValue(PAGE_TYPE.COMMON, "validation.txtChecking"),
            checkedSlug: null,
        });
        try {
            const taken = await AuthApi.checkExistsSlug({ slug });
            if (seq !== profileSlugCheckSeqRef.current) {
                return;
            }
            if (taken) {
                setProfileSlugValidation({
                    type: "error",
                    message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errExistsSlug"),
                    checkedSlug: null,
                });
                return;
            }
            setProfileSlugValidation({ type: "success", message: "", checkedSlug: slug });
        } catch (e) {
            console.error("StudentSignupContainer:validateProfileSlugOnBlur", e);
            if (seq === profileSlugCheckSeqRef.current) {
                setProfileSlugValidation({
                    type: "error",
                    message: getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errSlugCheckFailed"),
                    checkedSlug: null,
                });
            }
        }
    }, []);

    const validateProfileFields = () => {
        if (!imageInfo?.id) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errProfileImageSelection"));
            return true;
        }
        if (!studentParams.countryCode) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errCountrySelection"));
            return true;
        }
        if (!studentParams.firstName) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errFirstName"));
            return true;
        }
        if (!studentParams.lastName) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errLastName"));
            return true;
        }
        if (!studentParams.birthday) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errBirthdaySelection"));
            return true;
        }
        if (checkIsUnder14(studentParams.birthday) && !parentEmail) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errParentEmail"));
            return true;
        }
        if (checkIsUnder14(studentParams.birthday) && !checkIsEmail(parentEmail)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidEmail"));
            return true;
        }
        if (checkIsUnder14(studentParams.birthday) && parentEmail === accountParams.username) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errParentEmailAndUserEmailSame"));
            return true;
        }
        if (!studentParams.genderTypeCode) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errGenderSelection"));
            return true;
        }
        if (!(studentParams.slug || "").trim()) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errHandlerRequired"));
            return true;
        }
        return false;
    };

    const validateSlugForNext = async () => {
        const slug = (studentParams.slug || "").trim();
        if (!slug) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errHandlerRequired"));
            return true;
        }
        if (!checkStudentSlug(slug)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidSlug"));
            return true;
        }
        try {
            const taken = await AuthApi.checkExistsSlug({ slug });
            if (taken) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errExistsSlug"));
                return true;
            }
        } catch (e) {
            console.error("StudentSignupContainer:checkExistsSlug", e);
            return true;
        }
        return false;
    };

    const validateSlugForProfileStepNext = () => {
        const slug = (studentParams.slug || "").trim();
        if (!slug) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errHandlerRequired"));
            return true;
        }
        if (!checkStudentSlug(slug)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errInvalidSlug"));
            return true;
        }
        if (profileSlugValidation.type !== "success" || profileSlugValidation.checkedSlug !== slug) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errHandlerRequired"));
            return true;
        }
        return false;
    };

    const validateContactFields = () => {
        if (!studentParams.contactTypeCode) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errContactTypeCode"));
            return true;
        }
        if (!studentParams.contact) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errContact"));
            return true;
        }
        return false;
    };

    const validateLanguageRows = () => {
        if (languageRows.length === 0) {
            return false;
        }
        if (languageRows.some((r) => !r.languageCode)) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errLanguageIncomplete"));
            return true;
        }
        const selectedCodes = languageRows.map((r) => String(r.languageCode || "").trim()).filter(Boolean);
        const uniqueCodes = new Set(selectedCodes);
        if (selectedCodes.length !== uniqueCodes.size) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errDuplicateLanguage"));
            return true;
        }
        return false;
    };

    const validateTermsForSubmit = () => {
        if (!termList[0]?.checked) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errAgreeTermOfService"));
            return true;
        }
        if (!termList[1]?.checked) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errAgreePrivacyPolicy"));
            return true;
        }
        return false;
    };

    const buildOauthAccountStagePayload = () => {
        const displayName =
            `${studentParams.firstName || ""} ${studentParams.lastName || ""}`.trim() || accountParams.username.trim();
        return {
            uuid: oauthSignupMeta?.signupUuid,
            socialAccountType: oauthSignupMeta?.socialAccountType,
            username: accountParams.username.trim(),
            name: displayName,
            imageId: imageInfo?.id,
            socialId: oauthSignupMeta?.socialId,
            accountTypeCode: accountParams.accountTypeCode,
            termsOfService: !!termList[0]?.checked,
            personalPolicy: !!termList[1]?.checked,
            receiveEmail: !!termList[2]?.checked,
            receiveMessage: !!termList[3]?.checked,
        };
    };

    const extractJwtFromAccountResult = (accountResult: any) =>
        accountResult?.data?.data?.jwt ??
        accountResult?.data?.data?.jwtToken ??
        accountResult?.data?.jwt ??
        accountResult?.data?.jwtToken ??
        null;

    const setSpecialtyCodeList = () => {
        studentParams.specialtyCodeList = JSON.stringify(selectedSpecialtyCodes);
    };

    const handleWizardNext = async () => {
        if (wizardStep === "account") {
            if (validateAccountFields()) {
                return;
            }
            if (validateTermsForSubmit()) {
                return;
            }
            if (getAccessToken()) {
                dispatch(setIsLogin(true));
                dispatch(
                    setUserInfo({
                        accountTypeCode: "accountType:student",
                        hasDoneSignup: false,
                    } as any)
                );
                navigate("/student/signup", { replace: true });
                return;
            }
            if (!oauthSignupMeta) {
                try {
                    const res = await AuthApi.checkExistsUsername(accountParams.username.trim(), "accountType:student", "signupType:email");
                    if (res?.data?.data?.exists) {
                        alert(getLanguageValue(PAGE_TYPE.COMMON, "validation.txtExistingEmail"));
                        return;
                    }
                } catch (e) {
                    console.error("StudentSignupContainer:checkExistsUsername", e);
                    return;
                }
            }
            accountParams.termsOfService = !!termList[0]?.checked;
            accountParams.personalPolicy = !!termList[1]?.checked;
            accountParams.receiveEmail = !!termList[2]?.checked;
            accountParams.receiveMessage = !!termList[3]?.checked;

            setIsOnNetworking(true);
            try {
                let accountResult: any;
                if (oauthSignupMeta) {
                    accountResult = await AuthApi.signupBySocial(buildOauthAccountStagePayload());
                } else {
                    accountResult = await AuthApi.signupByEmail(accountParams);
                }
                const jwt = extractJwtFromAccountResult(accountResult);
                if (!jwt) {
                    return;
                }
                setAccessToken(jwt);
                dispatch(setIsLogin(true));
                dispatch(
                    setUserInfo({
                        accountTypeCode: "accountType:student",
                        hasDoneSignup: false,
                    } as any)
                );
                navigate("/student/signup", { replace: true });
            } catch (e: any) {
                if (e?.errorCode === "NEEDS_SOCIAL_LINK_CONFIRM") {
                    const sp = new URLSearchParams(location.search);
                    const p = (e as any).payload;
                    if (p?.accountDisplayName) {
                        sp.set("accountName", String(p.accountDisplayName));
                    }
                    if (p?.accountProfileSubUrl) {
                        sp.set("accountPictureSub", String(p.accountProfileSubUrl));
                    } else if (p?.accountProfileUrl) {
                        sp.set("accountPicture", String(p.accountProfileUrl));
                    }
                    navigate(`/signup/student/register/oauth-link?${sp.toString()}`, { replace: true });
                    return;
                }
                console.error("StudentSignupContainer:stage1 signup failed", e);
            } finally {
                setIsOnNetworking(false);
            }
            return;
        }
        if (wizardStep === "profile") {
            if (validateProfileFields()) {
                return;
            }
            if (validateSlugForProfileStepNext()) {
                return;
            }
            goToStep("basic");
            return;
        }
        if (wizardStep === "basic") {
            goToStep("specialty");
            return;
        }
        if (wizardStep === "specialty") {
            if (validateLanguageRows()) {
                return;
            }
            goToStep("contact");
            return;
        }
        if (wizardStep === "contact") {
            if (validateContactFields()) {
                return;
            }
            goToStep("confirm");
        }
    };

    const handleWizardBack = () => {
        if (wizardStep === "account") {
            cancelSignup();
            return;
        }
        const idx = REGISTER_STEPS.indexOf(wizardStep);
        if (idx <= 0) {
            return;
        }
        goToStep(REGISTER_STEPS[idx - 1]);
    };

    const createInfo = async () => {
        if (validateProfileFields()) {
            return;
        }
        if (validateContactFields()) {
            return;
        }
        if (await validateSlugForNext()) {
            return;
        }

        if (!btnRef.current) {
            return;
        }
        btnRef.current.disabled = true;

        try {
            if (!getAccessToken()) {
                btnRef.current.disabled = false;
                alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errMissingSignupSession"));
                return;
            }

            const isUnder14 = checkIsUnder14(studentParams.birthday);

            setSpecialtyCodeList();

            if (imageInfo?.id) {
                studentParams.profileImageId = imageInfo.id;
            }

            studentParams.imageIdList = imageIdList;

            studentParams.under14 = isUnder14 ? 1 : 0;
            studentParams.isActive = isUnder14 ? 0 : 1;

            studentParams.languageList = languageRows
                .filter((r) => r.languageCode && r.level)
                .map((r) => ({ languageCode: r.languageCode, languageLevelCode: r.level }));

            const studentResult = await AuthApi.createInfo("student", studentParams);
            const isCreateInfoSuccess = studentResult?.status === 200;

            if (isCreateInfoSuccess) {
                if (isUnder14) {
                    await AuthApi.sendVerifyParentEmail({
                        parentEmail,
                    });
                }

                // Keep current login session and refresh user state so router guard does not bounce back to /student/signup.
                try {
                    const myInfoResult = await AuthApi.getMyInfo();
                    if (myInfoResult?.info) {
                        if (myInfoResult.info.accountTypeCode === "accountType:student") {
                            try {
                                const studentInfo = await StudentApi.getInfoForStudent();
                                myInfoResult.info.profileImage = studentInfo?.info?.profileImageInfo?.url || null;
                            } catch (e) {
                                console.error("StudentSignupContainer:createInfo refresh student profile failed", e);
                                myInfoResult.info.profileImage = null;
                            }
                        }
                        dispatch(setIsLogin(true));
                        dispatch(setUserInfo(myInfoResult.info));
                    } else {
                        dispatch(setIsLogin(true));
                        dispatch(
                            setUserInfo({
                                accountTypeCode: "accountType:student",
                                hasDoneSignup: true,
                            } as any)
                        );
                    }
                } catch (e) {
                    console.error("StudentSignupContainer:createInfo refresh myInfo failed", e);
                    dispatch(setIsLogin(true));
                    dispatch(
                        setUserInfo({
                            accountTypeCode: "accountType:student",
                            hasDoneSignup: true,
                        } as any)
                    );
                }

                btnRef.current.disabled = false;
                setIsDone(true);
            } else {
                btnRef.current.disabled = false;
            }
        } catch (e) {
            console.error("StudentSignupContainer:createInfo failed", e);
            btnRef.current.disabled = false;
        }
    };

    useEffect(() => {
        getTermList();
    }, []);

    useEffect(() => {
        const p = (location.pathname || "/").replace(/\/+$/, "") || "/";
        if (!p.startsWith(registerBasePath) || (!forceInfoRegistrationFlow && p === `${registerBasePath}/oauth-link`)) {
            return;
        }
        const desiredPath = wizardStep === "account" ? accountPath : profilePath;
        if (p !== desiredPath) {
            navigate(`${desiredPath}${location.search}`, { replace: true });
        }
    }, [wizardStep, location.pathname, location.search, navigate, registerBasePath, accountPath, profilePath, forceInfoRegistrationFlow]);

    useEffect(() => {
        if (wizardStep !== "account" || forceInfoRegistrationFlow) {
            return;
        }
        const token = getAccessToken();
        if (!token) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const my = await AuthApi.getMyInfo();
                if (cancelled || !my?.info) {
                    return;
                }
                if (my.info.accountTypeCode === "accountType:student" && my.info.hasDoneSignup === false) {
                    navigate("/student", { replace: true });
                }
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [wizardStep, location.search, navigate, forceInfoRegistrationFlow]);

    useEffect(() => {
        if (wizardStep !== "account" || forceInfoRegistrationFlow) {
            return;
        }
        const sp = new URLSearchParams(location.search);
        const oauthError = sp.get("oauthError");
        const oauthEmail = sp.get("oauthEmail");
        if (!oauthError && !oauthEmail) {
            return;
        }
        if (oauthError) {
            alert(resolveOauthErrorMessage(oauthError));
            setOauthSignupMeta(null);
            const schoolId = sp.get("school-id");
            const next = new URLSearchParams();
            if (schoolId) {
                next.set("school-id", schoolId);
            }
            const qs = next.toString();
            navigate(
                {
                    pathname: "/signup/student",
                    search: qs ? `?${qs}` : "",
                },
                { replace: true, state: { allowSignupMethodScreen: true } }
            );
            return;
        }
        if (!oauthEmail) {
            return;
        }

        let cancelled = false;
        (async () => {
            const oauthSocialId = sp.get("oauthSocialId");
            const oauthProvider = sp.get("oauthProvider");
            const oauthSignupUuid = sp.get("oauthSignupUuid");

            setAccountParams((prev: any) => ({ ...prev, username: oauthEmail }));
            if (oauthSocialId && oauthProvider) {
                setOauthSignupMeta({
                    socialId: oauthSocialId,
                    socialAccountType: oauthProviderToSocialAccountType(oauthProvider),
                    signupUuid: oauthSignupUuid || undefined,
                });
            } else {
                setOauthSignupMeta(null);
            }

            const schoolId = sp.get("school-id");
            const next = new URLSearchParams();
            if (schoolId) {
                next.set("school-id", schoolId);
            }

            if (oauthSocialId && oauthProvider) {
                const linkDisplay = await AuthApi.getOauthLinkAccountDisplay(oauthEmail);
                if (!cancelled && linkDisplay?.status === 200) {
                    next.set("oauthEmail", oauthEmail);
                    next.set("oauthProvider", oauthProvider);
                    next.set("oauthSocialId", oauthSocialId);
                    if (oauthSignupUuid) {
                        next.set("oauthSignupUuid", oauthSignupUuid);
                    }
                    navigate(`${registerBasePath}/oauth-link?${next.toString()}`, { replace: true });
                    return;
                }
            }

            if (!cancelled) {
                const qs = next.toString();
                navigate({ pathname: accountPath, search: qs ? `?${qs}` : "" }, { replace: true });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [wizardStep, location.search, navigate, accountPath, forceInfoRegistrationFlow, registerBasePath]);

    const isProfileNextEnabled = useMemo(() => {
        if (!imageInfo?.id) {
            return false;
        }
        if (!studentParams.countryCode) {
            return false;
        }
        if (!String(studentParams.firstName || "").trim()) {
            return false;
        }
        if (!String(studentParams.lastName || "").trim()) {
            return false;
        }
        if (!studentParams.birthday) {
            return false;
        }
        if (checkIsUnder14(studentParams.birthday)) {
            const pe = String(parentEmail || "").trim();
            if (!pe || !checkIsEmail(pe) || pe === accountParams.username) {
                return false;
            }
        }
        if (!studentParams.genderTypeCode) {
            return false;
        }
        const slug = String(studentParams.slug || "").trim();
        if (!slug) {
            return false;
        }
        if (profileSlugValidation.type !== "success" || profileSlugValidation.checkedSlug !== slug) {
            return false;
        }
        return true;
    }, [
        imageInfo?.id,
        studentParams.countryCode,
        studentParams.firstName,
        studentParams.lastName,
        studentParams.birthday,
        studentParams.genderTypeCode,
        studentParams.slug,
        parentEmail,
        accountParams.username,
        profileSlugValidation.type,
        profileSlugValidation.checkedSlug,
    ]);

    const q = location.search;
    const phase1Token = !!getAccessToken();
    const toSignupMethodScreen: string | { pathname: string; search?: string; state: { allowSignupMethodScreen: boolean } } =
        phase1Token
            ? { pathname: signupMethodPath, search: q || undefined, state: { allowSignupMethodScreen: true } }
            : `${signupMethodPath}${q}`;

    const headerBackUrl = wizardStep === "account" ? toSignupMethodScreen : `${accountPath}${q}`;

    const showAccountTermsLoading = wizardStep === "account" && (!termsReady || termList.length === 0);
    if (showAccountTermsLoading) {
        return <GlobalLoadingContainer message={getLanguageValue(PAGE_TYPE.COMMON, "appInit.txtPleaseWait")} />;
    }

    const isPasswordRulePassed = !!accountParams.password && checkPassword(accountParams.password);
    const isPasswordConfirmed =
        !!accountParams.password &&
        !!accountParams.confirmedPassword &&
        accountParams.password === accountParams.confirmedPassword;
    const isAccountNextEnabled =
        !!oauthSignupMeta || (isUsernameAvailable && isPasswordRulePassed && isPasswordConfirmed);

    return (
        !isDone ? (
            <>
                <StudentSignupComponent
                    btnRef={btnRef}
                    currentStep={wizardStep}
                    oauthSignupMeta={oauthSignupMeta}
                    accountParams={accountParams}
                    studentParams={studentParams}
                    parentEmail={parentEmail}
                    setParentEmail={setParentEmail}
                    checkIsUnder14={checkIsUnder14}
                    imageInfo={imageInfo}
                    termList={termList}
                    isOnNetworking={isOnNetworking}
                    selectedSpecialtyCodes={selectedSpecialtyCodes}
                    languageRows={languageRows}
                    addLanguageRow={addLanguageRow}
                    removeLanguageRow={removeLanguageRow}
                    updateLanguageRow={updateLanguageRow}
                    onDevAutofillClick={applyStudentSignupDevAutofill}
                    onChangeInput={onChangeInput}
                    onBlurUsername={validateUsernameOnBlur}
                    onBlurPassword={validatePasswordOnBlur}
                    onBlurConfirmedPassword={validateConfirmedPasswordOnBlur}
                    usernameValidationType={usernameValidation.type}
                    usernameValidationMessage={usernameValidation.message}
                    passwordValidationType={passwordValidation.type}
                    passwordValidationMessage={passwordValidation.message}
                    confirmedPasswordValidationType={confirmedPasswordValidation.type}
                    confirmedPasswordValidationMessage={confirmedPasswordValidation.message}
                    isAccountNextEnabled={isAccountNextEnabled}
                    isProfileNextEnabled={isProfileNextEnabled}
                    onBlurProfileSlug={validateProfileSlugOnBlur}
                    profileSlugValidationType={profileSlugValidation.type}
                    profileSlugValidationMessage={profileSlugValidation.message}
                    onChangeCheckbox={onChangeCheckbox}
                    onProfileImageFileChange={onProfileImageFileChange}
                    onClickBtnCancelSignup={cancelSignup}
                    onWizardNext={handleWizardNext}
                    onWizardBack={handleWizardBack}
                    onClickBtnSignup={createInfo}
                    headerBackUrl={headerBackUrl}
                    isInfoRegistrationFlow={wizardStep !== "account"}
                />
                {cropObjectUrl && (
                    <PublicProfileImageCropModalComponent
                        imageSrc={cropObjectUrl}
                        onClose={closeCropModal}
                        onApply={onCropModalApply}
                    />
                )}
            </>
        ) : (
            <StudentSignupDoneComponent
                accountParams={accountParams}
                studentParams={studentParams}
                parentEmail={parentEmail}
                imageInfo={imageInfo}
                checkIsUnder14={checkIsUnder14}
                hideEmailVerificationNotice={
                    !!oauthSignupMeta || userInfo?.signupTypeCode === "signupType:socialAccount"
                }
            />
        )
    );
};

export default StudentSignupContainer;
